import { pricesApi } from '@gl/api-client';
import type { PriceBatchesQuery, PriceHistoryQuery, PriceRevertDto } from '@gl/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError, errorMessage } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import type { CatalogResponse } from './types';

export const prices = pricesApi(api);

export const pricesKeys = {
  all: (localId: string) => [...queryKeys.local(localId), 'prices'] as const,
  history: (localId: string, q: PriceHistoryQuery) => [...pricesKeys.all(localId), 'history', q] as const,
  batches: (localId: string, q: PriceBatchesQuery) => [...pricesKeys.all(localId), 'batches', q] as const,
  catalog: (localId: string) => [...queryKeys.local(localId), 'catalog'] as const,
};

export function useCatalog(localId: string) {
  return useQuery({
    queryKey: pricesKeys.catalog(localId),
    queryFn: () => api.get<CatalogResponse>(`/locals/${encodeURIComponent(localId)}/catalog`),
  });
}

export function useHistory(localId: string, q: PriceHistoryQuery) {
  return useQuery({
    queryKey: pricesKeys.history(localId, q),
    queryFn: () => prices.history(localId, q),
    placeholderData: keepPreviousData,
  });
}

export function useBatches(localId: string, q: PriceBatchesQuery) {
  return useQuery({
    queryKey: pricesKeys.batches(localId, q),
    queryFn: () => prices.batches(localId, q),
    placeholderData: keepPreviousData,
  });
}

export function useInvalidatePrices(localId: string) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: pricesKeys.catalog(localId) }),
      qc.invalidateQueries({ queryKey: pricesKeys.all(localId) }),
    ]);
}

export function revertSummary(r: PriceRevertDto): string {
  if (r.count === 0) return 'No se revirtió nada: todos los precios cambiaron después del lote';
  const base = r.count === 1 ? 'Se revirtió 1 precio' : `Se revirtieron ${r.count} precios`;
  if (r.skipped.length === 0) return base;
  const n = r.skipped.length;
  return `${base}; ${n === 1 ? '1 se salteó porque cambió' : `${n} se saltearon porque cambiaron`} después`;
}

export function useRevertBatch(localId: string, opts: { onSuccess?: (r: PriceRevertDto) => void } = {}) {
  const invalidate = useInvalidatePrices(localId);
  return useMutation({
    mutationFn: (batchId: string) => prices.revert(localId, batchId),
    onSuccess: (r) => {
      void invalidate();
      opts.onSuccess?.(r);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'BATCH_ALREADY_REVERTED') {
        toast.error('Ese lote ya fue revertido');
        void invalidate();
      } else {
        toast.error(errorMessage(err));
      }
    },
  });
}
